import { Box } from '@mui/system';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import AuthFormWrapper from 'src/components/templates/auth/auth-form-wrapper';
import { useKeyDown } from 'src/hooks/use-key-down';
import { Logo } from 'src/icons/logo';
import AuthFormHeader from '../auth-form-header';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'src/icons/copy-to-clipboard';
import toast, { LoaderIcon } from 'react-hot-toast';
import {
  MfaSetupFormValues,
  useMfaSetupDialogValidationSchema,
} from './schema/use-form-validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUserStore } from 'src/store/user-store';
import { useVerifyAndActivateMfa } from 'src/hooks/mfa/use-verify-and-activate-mfa';
import { QRCodeSVG } from 'qrcode.react';
import { generateTotpSecret } from 'src/utils/generate-totp-secret';
import { generateTotpSecretQrUrl } from 'src/utils/generate-totp-secret-qr-url';
import { useEffect, useRef, useState } from 'react';
import useSelectedSite from 'src/hooks/use-selected-site';
import { useSignOut } from 'src/hooks/api/auth/use-sign-out';

const googleAuthenticatorURL = 'https://safety.google/safety/authentication/';
const authyURL = 'https://www.authy.com/download/';

const defaultValues = {
  code: '',
};

type Props = {
  open: boolean;
  handleClose: () => void;
  userEmail?: string;
};

export const MfaSetupDialog = ({ open, handleClose, userEmail }: Props) => {
  const { t } = useTranslation();

  const selectedSite = useSelectedSite();

  const totpSecretRef = useRef<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    if (!open || !userEmail) return;

    const secret = totpSecretRef.current ?? generateTotpSecret();
    totpSecretRef.current = secret;

    const qrCodeUrl = generateTotpSecretQrUrl(
      secret,
      userEmail,
      `HiLight${selectedSite?.name ? ` (${selectedSite.name})` : ''}`,
    );

    setQrCodeUrl(qrCodeUrl);
  }, [open, userEmail, selectedSite?.name]);

  const { signOut, isSigningOut } = useSignOut();

  const { setMfaEnabled } = useUserStore();

  const validationSchema = useMfaSetupDialogValidationSchema();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<MfaSetupFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const onSuccess = () => {
    setMfaEnabled(true);
  };

  const {
    mutate: verifyAndActivateMfa,
    isPending: isVerifyingAndActivatingMfa,
    error: verificationError,
  } = useVerifyAndActivateMfa(onSuccess);

  const onSubmit = (values: MfaSetupFormValues) => {
    verifyAndActivateMfa({
      secret: totpSecretRef.current || '',
      mfaCode: values.code,
    });
  };

  const onBackButtonClick = () => {
    signOut(handleClose);
  };

  useKeyDown('Enter', handleSubmit(onSubmit));

  return (
    <Dialog open={open}>
      <DialogContent sx={{ p: 0 }}>
        <AuthFormWrapper
          actionsSlot={
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}>
              <Button
                variant="outlined"
                onClick={onBackButtonClick}
                disabled={isSigningOut}>
                {isSigningOut ? (
                  <LoaderIcon style={{ width: 24.5, height: 24.5 }} />
                ) : (
                  t('auth:login.backToLogin')
                )}
              </Button>
              <LoadingButton
                variant="contained"
                size="medium"
                disabled={!isValid || isVerifyingAndActivatingMfa}
                loading={isVerifyingAndActivatingMfa}
                onClick={handleSubmit(onSubmit)}>
                {t('auth:login.submitButton')}
              </LoadingButton>
            </Box>
          }>
          <AuthFormHeader
            title={t('auth:login.totpTitle')}
            description={t('auth:login.contentTotp')}
            icon={<Logo sx={{ width: '234px', height: '61px' }} />}
          />
          <ol
            style={{
              position: 'relative',
              paddingInlineStart: 16,
              marginBottom: 0,
            }}>
            <li
              style={{
                fontSize: 14,
              }}>
              <Typography
                color="textPrimary"
                variant="body2"
                sx={{ fontSize: 14, textAlign: 'start' }}>
                <Trans
                  i18nKey={'auth:login.totp.qrCodeScan'}
                  components={{
                    ga: (
                      <Link
                        to={googleAuthenticatorURL}
                        target="_blank"
                        style={{ color: 'inherit' }}
                      />
                    ),
                    authy: (
                      <Link
                        to={authyURL}
                        target="_blank"
                        style={{ color: 'inherit' }}
                      />
                    ),
                  }}
                />
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  justifyContent: 'center',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    aligItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    mb: 2,
                  }}>
                  <MfaQrCode qrCodeUrl={qrCodeUrl} />
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 24,
                  }}>
                  <Typography
                    color="textPrimary"
                    variant="body2"
                    sx={{ fontSize: 12 }}>
                    {totpSecretRef.current}
                  </Typography>
                  <CopyToClipboard
                    sx={{ color: 'neutral.700', cursor: 'pointer' }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          totpSecretRef?.current || '',
                        );
                        toast.success(t('common:copiedToClipboard'));
                      } catch {
                        toast.error(t('common:copyToClipboardFailed'));
                      }
                    }}
                  />
                </Box>
              </Box>
            </li>
            <li style={{ fontSize: 14, marginBottom: 36 }}>
              <Typography
                color="textPrimary"
                variant="body2"
                sx={{ fontSize: 14, textAlign: 'start' }}>
                {t('auth:login.totp.code')}
              </Typography>
              <Controller
                name="code"
                control={control}
                render={({
                  field: { onChange, onBlur, value, ref },
                  fieldState: { error },
                }) => (
                  <InputField
                    fullWidth
                    required
                    inputRef={ref}
                    onBlur={onBlur}
                    onChange={onChange}
                    type="text"
                    value={value}
                    error={!!error}
                    helperText={error?.message}
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </li>
            {verificationError?.response?.data?.message ? (
              <Alert severity="error">
                {verificationError?.response?.data?.message}
              </Alert>
            ) : null}
          </ol>
        </AuthFormWrapper>
      </DialogContent>
    </Dialog>
  );
};

type MfaQrCodeProps = {
  qrCodeUrl: string;
};

const MfaQrCode = ({ qrCodeUrl }: MfaQrCodeProps) => {
  return <QRCodeSVG value={qrCodeUrl} size={150} marginSize={2} />;
};
